import { ArrowLeftRight, Loader2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { describeErrorText } from '@/lib/errors';
import { shortenAddress } from '@/lib/utils';

import { resolveSwitchToAddress } from './resolveSwitchToAddress';
import { pairKey, useWalletSwitchStore, type WalletSwitchPair } from './store';

/**
 * Raised by {@link WalletSwitchBridge} when the connected wallet no longer
 * matches the account the login belongs to. Two ways out, both of them
 * explicit: move over to the newly selected account, or stay put.
 */
const WalletSwitchDialog: React.FC = () => {
    const pending = useWalletSwitchStore((state) => state.pending);
    const dismiss = useWalletSwitchStore((state) => state.dismiss);
    const [isSwitching, setIsSwitching] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const key = pending ? pairKey(pending) : null;
    // a different switch is a different question: it must not inherit the
    // failure text or the busy state of the one before it. Only a NEW prompt
    // resets: the prompt going away is what signing out looks like from here
    // (the login loses its owner, so the watcher clears it), and resetting on
    // that would cancel the busy state in the middle of the switch it belongs
    // to.
    useEffect(() => {
        if (!key) return;
        setError(null);
        setIsSwitching(false);
    }, [key]);

    // the copy outlives the prompt by one close animation, so the addresses do
    // not blank out while the dialog is sliding away.
    const [shown, setShown] = useState<WalletSwitchPair | null>(pending);
    useEffect(() => {
        if (pending) setShown(pending);
    }, [pending]);

    const currentOwner = shown?.currentOwner ?? null;
    const nextAddress = shown?.nextAddress ?? null;
    // The OAuth projection is the weakest owner source: it names the EOA the
    // social account was created against, and says nothing about which wallet
    // the user meant to be holding right now. That cohort also funds deposits
    // from whatever account is connected, so signing them out is the answer
    // they are least likely to want: keeping the account leads instead, and the
    // destructive option steps back to secondary.
    const keepLeads = shown?.ownerSource === 'oauth';

    const handleSwitch = async () => {
        if (!pending) return;
        const target = pending.nextAddress;
        setError(null);
        setIsSwitching(true);
        try {
            await resolveSwitchToAddress(target);
            dismiss();
            setIsSwitching(false);
        } catch (caught) {
            // the logout leg failed, so nothing was revoked: the user is still
            // signed in and can simply try again or keep the account.
            setError(
                describeErrorText(caught, { operation: 'Account switch' }),
            );
            setIsSwitching(false);
        }
    };

    // The two answers, built once and ordered below: whichever comes second is
    // the primary one, so only the emphasis changes between cohorts, never the
    // wording or what the button does.
    const keepButton = (
        <Button
            key="keep"
            variant={keepLeads ? 'default' : 'secondary'}
            onClick={() => dismiss()}
            disabled={isSwitching}
            title={currentOwner ?? undefined}
        >
            Keep using {currentOwner ? shortenAddress(currentOwner) : ''}
        </Button>
    );
    const switchButton = (
        <Button
            key="switch"
            variant={keepLeads ? 'secondary' : 'default'}
            onClick={() => void handleSwitch()}
            disabled={isSwitching}
            title={nextAddress ?? undefined}
        >
            {isSwitching && <Loader2 className="animate-spin" />}
            Sign out and continue with{' '}
            {nextAddress ? shortenAddress(nextAddress) : ''}
        </Button>
    );

    return (
        <Dialog
            // `isSwitching` keeps it up on purpose: signing out clears the
            // login's owner, which takes the prompt down, and the user would
            // otherwise watch the window vanish with no sign that the on-chain
            // lookup deciding where they land is still running.
            open={!!pending || isSwitching}
            onOpenChange={(open) => {
                if (!open && !isSwitching) dismiss();
            }}
        >
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ArrowLeftRight className="size-4 text-primary" />
                        Wallet switch detected
                    </DialogTitle>
                    <DialogDescription>
                        Your wallet is now on{' '}
                        <span
                            className="font-mono text-foreground"
                            title={nextAddress ?? undefined}
                        >
                            {nextAddress ? shortenAddress(nextAddress) : ''}
                        </span>
                        , while you are signed in as{' '}
                        <span
                            className="font-mono text-foreground"
                            title={currentOwner ?? undefined}
                        >
                            {currentOwner ? shortenAddress(currentOwner) : ''}
                        </span>
                        . Trading stays on the account you signed in with until
                        you switch over.
                    </DialogDescription>
                </DialogHeader>
                <p className="text-xs text-muted-foreground">
                    Keeping your current account leaves the wallet where it is,
                    so anything the wallet itself signs (a deposit, for example)
                    would come from{' '}
                    <span
                        className="font-mono"
                        title={nextAddress ?? undefined}
                    >
                        {nextAddress ? shortenAddress(nextAddress) : ''}
                    </span>
                    .
                </p>
                {error && (
                    <p className="text-xs text-destructive" role="alert">
                        {error}
                    </p>
                )}
                <DialogFooter>
                    {keepLeads ? (
                        <>
                            {switchButton}
                            {keepButton}
                        </>
                    ) : (
                        <>
                            {keepButton}
                            {switchButton}
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default WalletSwitchDialog;
